
import { useRef, useEffect, useCallback } from 'react';
import EditorToolbar from './EditorToolbar';

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  onImageUpload: (file: File) => Promise<string | null>;
  contentUploading: boolean;
}

export default function RichTextEditor({
  content,
  onChange,
  onImageUpload,
  contentUploading,
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const isInternalChange = useRef(false);
  const lastContent = useRef(content);
  const initialized = useRef(false);

  // 初回のみ外部contentをエディタに反映
  useEffect(() => {
    if (!editorRef.current) return;
    if (!initialized.current) {
      editorRef.current.innerHTML = content || '';
      lastContent.current = content;
      initialized.current = true;
    }
  }, [content]);

  // 外部からcontentが変わった場合（テンプレート挿入など）
  useEffect(() => {
    if (!editorRef.current || !initialized.current) return;
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    if (content !== lastContent.current) {
      const sel = window.getSelection();
      const hadFocus = editorRef.current.contains(document.activeElement);

      editorRef.current.innerHTML = content || '';
      lastContent.current = content;

      if (hadFocus) {
        editorRef.current.focus();
        // カーソルを末尾に移動
        if (sel) {
          const range = document.createRange();
          range.selectNodeContents(editorRef.current);
          range.collapse(false);
          sel.removeAllRanges();
          sel.addRange(range);
        }
      }
    }
  }, [content]);

  const handleInput = useCallback(() => {
    if (!editorRef.current) return;
    const html = editorRef.current.innerHTML;
    isInternalChange.current = true;
    lastContent.current = html;
    onChange(html);
  }, [onChange]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const text =
      e.clipboardData.getData('text/html') ||
      e.clipboardData.getData('text/plain');

    // 安全なHTMLのみ許可
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = text;

    // scriptタグなどを除去
    tempDiv
      .querySelectorAll('script, style, iframe, object, embed')
      .forEach((el) => el.remove());

    const cleanHtml = tempDiv.innerHTML;
    document.execCommand('insertHTML', false, cleanHtml);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Tab キーでインデント
    if (e.key === 'Tab') {
      e.preventDefault();
      document.execCommand('insertHTML', false, '&nbsp;&nbsp;&nbsp;&nbsp;');
    }
  }, []);

  const execCommand = useCallback(
    (command: string, value?: string) => {
      editorRef.current?.focus();
      document.execCommand(command, false, value);
      handleInput();
    },
    [handleInput]
  );

  const insertHtmlAtCursor = useCallback(
    (html: string) => {
      editorRef.current?.focus();
      document.execCommand('insertHTML', false, html);
      handleInput();
    },
    [handleInput]
  );

  const handleFileUpload = useCallback(
    async (file: File) => {
      try {
        const url = await onImageUpload(file);
        if (url) {
          insertHtmlAtCursor(
            `<img src="${url}" alt="記事内画像" style="max-width:100%;border-radius:8px;margin:16px 0;" />`
          );
        }
      } catch (error) {
        console.error('Image upload failed:', error);
      }
    },
    [onImageUpload, insertHtmlAtCursor]
  );

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
      <EditorToolbar
        execCommand={execCommand}
        insertHtmlAtCursor={insertHtmlAtCursor}
        onFileUpload={handleFileUpload}
        contentUploading={contentUploading}
        editorRef={editorRef}
      />
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        className="rich-editor min-h-[400px] max-h-[600px] overflow-y-auto px-5 py-4 text-sm text-gray-800 leading-relaxed outline-none focus:ring-2 focus:ring-teal-500/20"
        onInput={handleInput}
        onPaste={handlePaste}
        onKeyDown={handleKeyDown}
        data-placeholder="記事の本文を入力してください..."
      />
    </div>
  );
}
